/**
 * Seitenbaum flach ausrollen — reine Funktion, ohne DOM prüfbar.
 *
 * Die Seitenliste hieß „Seitenbaum" und war eine flache Aufzählung: Die API
 * liefert `elternId` und `sortierung` mit, die Oberfläche hat beides
 * weggeworfen. Bei mehr als einer Handvoll Seiten ist damit nicht mehr zu
 * erkennen, was wozu gehört.
 *
 * Ausgerollt statt verschachtelt, weil die Liste eine Liste bleiben soll:
 * eine flache `ul` mit Einrückung ist für Tastatur und Vorlesesoftware eine
 * Reihe gleichrangiger Einträge mit Stufenangabe — verschachtelte `ul` in
 * `li` wären ein Baum, den man aufklappen können müsste. Das ist ein
 * eigenes Feature und nicht Teil dieses Durchgangs.
 */

export type BaumKnoten = {
  id: string;
  titel: string;
  elternId: string | null;
  sortierung: string;
};

export type BaumZeile<T extends BaumKnoten> = {
  knoten: T;
  /** 0 für Wurzeln. */
  tiefe: number;
};

/**
 * Geschwister sortieren wie der Server: Bruchindex, dann Titel, dann Kennung.
 * Die Reihenfolge muss mit `sortiereBaum` im Dienst übereinstimmen, sonst
 * springt die Liste beim Neuladen.
 */
function sortiere<T extends BaumKnoten>(zeilen: T[]): T[] {
  return [...zeilen].sort((a, b) => {
    if (a.sortierung !== b.sortierung) {
      return a.sortierung < b.sortierung ? -1 : 1;
    }
    const t = a.titel.localeCompare(b.titel, "de");
    if (t !== 0) return t;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/**
 * Rollt die Knoten in Anzeigereihenfolge aus.
 *
 * Robust gegen zwei Fälle, die aus echten Daten kommen können:
 *
 * - **Waise:** `elternId` zeigt auf eine Seite, die nicht in der Liste ist
 *   (archiviert oder nicht sichtbar). Der Knoten wird zur Wurzel — er
 *   verschwindet nicht, denn dann fehlte er in der Oberfläche ganz.
 * - **Zyklus:** A ist Kind von B und B von A. Besuchte Knoten werden
 *   festgehalten; wer schon dran war, wird nicht erneut ausgerollt. Ohne das
 *   hinge die Seite in einer Endlosschleife.
 */
export function rolleBaumAus<T extends BaumKnoten>(
  knoten: readonly T[],
): BaumZeile<T>[] {
  const vorhanden = new Set(knoten.map((k) => k.id));
  const kinder = new Map<string | null, T[]>();

  for (const k of knoten) {
    // Waise: Elternteil nicht in der Liste → als Wurzel einhängen.
    const schluessel =
      k.elternId && vorhanden.has(k.elternId) ? k.elternId : null;
    const liste = kinder.get(schluessel) ?? [];
    liste.push(k);
    kinder.set(schluessel, liste);
  }

  const ausgabe: BaumZeile<T>[] = [];
  const besucht = new Set<string>();

  const gehe = (elternId: string | null, tiefe: number): void => {
    for (const k of sortiere(kinder.get(elternId) ?? [])) {
      if (besucht.has(k.id)) continue;
      besucht.add(k.id);
      ausgabe.push({ knoten: k, tiefe });
      gehe(k.id, tiefe + 1);
    }
  };

  gehe(null, 0);

  /*
   * Was ein Zyklus verschluckt hat, kommt hier hinterher — lieber
   * unsortiert sichtbar als still verschwunden.
   */
  for (const k of knoten) {
    if (!besucht.has(k.id)) {
      besucht.add(k.id);
      ausgabe.push({ knoten: k, tiefe: 0 });
    }
  }

  return ausgabe;
}
